import { Button, ButtonProps } from '@chakra-ui/react';
import React from 'react';
import {
  FiArrowLeft,
  FiCheck,
  FiDelete,
  FiDownload,
  FiEdit,
  FiEye,
  FiPlus,
  FiRotateCcw,
  FiSave,
  FiSearch,
  FiSlash,
} from 'react-icons/fi';
import { MutateOptions } from 'react-query';

import { AppError } from '../../framework/models/errors';
import { TypeUtils } from '../../framework/utils/UtilityTypes';
import {
  DownloadAttachedFileQuery,
  DownloadAttachedResult,
  useGetAttachedFile,
  useGetAttachedFileDownload,
} from '../hooks/queries/attachedFile';
import useToastHook from '../hooks/ToastHook';
import { EngeneFileObject } from '../models/EngeneFileObject';

/**
 * 統一スタイルボタン
 * @param props -
 * @returns
 */
const StyledButton: React.FunctionComponent<ButtonProps> = ({
  children,
  disabled,
  ...props
}) => (
  <Button
    boxShadow={
      props.variant !== 'link' ? '4px 3px 5px 0px rgba(0,0,0,0.35)' : undefined
    }
    colorScheme="blue"
    isDisabled={props.isDisabled || disabled}
    paddingX={4}
    paddingY={2}
    textDecoration={props.variant === 'link' ? 'underline' : undefined}
    {...props}
  >
    {children}
  </Button>
);

type ButtonPropsWithoutChildren = Omit<ButtonProps, 'children'>;

/**
 * 追加ボタン
 * @param props -
 * @returns
 */
export const AddButton: React.FunctionComponent<ButtonPropsWithoutChildren> = (
  props
) => (
  <StyledButton leftIcon={<FiPlus />} backgroundColor="gray.500" {...props}>
    追加
  </StyledButton>
);

/**
 * 同意ボタン
 * @param props -
 * @returns
 */
export const AgreeButton: React.FunctionComponent<
  ButtonPropsWithoutChildren
> = (props) => (
  <CheckButton type="submit" {...props}>
    同意
  </CheckButton>
);

/**
 * 戻るボタン
 * @param props -
 * @returns
 */
export const BackButton: React.FunctionComponent<ButtonPropsWithoutChildren> = (
  props
) => (
  <StyledButton
    backgroundColor="gray.500"
    leftIcon={<FiArrowLeft />}
    {...props}
  >
    戻る
  </StyledButton>
);

const CheckButton: React.FunctionComponent<ButtonProps> = ({
  children,
  ...props
}) => (
  <StyledButton leftIcon={<FiCheck />} {...props}>
    {children}
  </StyledButton>
);

/**
 * クリアボタン
 * @param props -
 * @returns
 */
export const ClearButton: React.FunctionComponent<
  ButtonPropsWithoutChildren
> = (props) => (
  <StyledButton
    backgroundColor="gray.500"
    leftIcon={<FiRotateCcw />}
    {...props}
  >
    クリア
  </StyledButton>
);

/**
 * 閉じるボタン
 * @param props -
 * @returns
 */
export const CloseButton: React.FunctionComponent<
  ButtonPropsWithoutChildren
> = (props) => (
  <StyledButton leftIcon={<FiSlash />} {...props}>
    閉じる
  </StyledButton>
);

/**
 * 了承ボタン
 * @param props -
 * @returns
 */
export const CommitButton: React.FunctionComponent<
  ButtonPropsWithoutChildren
> = (props) => (
  <CheckButton type="submit" {...props}>
    了承
  </CheckButton>
);

/**
 * 新規作成ボタン
 * @param props -
 * @returns
 */
export const CreateButton: React.FunctionComponent<
  ButtonPropsWithoutChildren
> = (props) => (
  <StyledButton leftIcon={<FiPlus />} {...props}>
    新規
  </StyledButton>
);

/**
 * 確定ボタン
 * @param props -
 * @returns
 */
export const DecideButton: React.FunctionComponent<
  ButtonPropsWithoutChildren
> = (props) => (
  <CheckButton type="submit" {...props}>
    確定
  </CheckButton>
);

/**
 * 削除ボタン
 * @param props -
 * @returns
 */
export const DeleteButton: React.FunctionComponent<
  ButtonPropsWithoutChildren
> = (props) => (
  <StyledButton colorScheme="red" leftIcon={<FiDelete />} {...props}>
    削除
  </StyledButton>
);

type DownloadButtonProps = ButtonPropsWithoutChildren & {
  fileName?: string;
  onError?: MutateOptions<
    void | DownloadAttachedResult,
    unknown,
    DownloadAttachedFileQuery,
    unknown
  >['onError'];
  query: DownloadAttachedFileQuery;
};

/**
 * ダウンロードボタン
 * @param props -
 * @returns
 */
export const DownloadButton: React.FunctionComponent<DownloadButtonProps> = ({
  fileName,
  onError,
  query,
  ...props
}) => {
  const getAttachedFile = useGetAttachedFileDownload();
  const { toast } = useToastHook();

  return (
    <StyledButton
      backgroundColor="gray.500"
      leftIcon={<FiDownload />}
      onClick={() =>
        getAttachedFile.mutate(query, {
          onSuccess: (result) => {
            if (!TypeUtils.isNil(result)) {
              const a = document.createElement('a');
              a.href = window.URL.createObjectURL(result.file);
              a.download = fileName || result.filename;
              a.click();
            }
          },
          onError:
            onError ||
            ((error) =>
              toast(
                error instanceof AppError
                  ? error
                  : 'ファイルダウンロードに失敗しました'
              )),
        })
      }
      {...props}
    >
      ダウンロード
    </StyledButton>
  );
};

/**
 * 編集ボタン
 * @param props -
 * @returns
 */
export const EditButton: React.FunctionComponent<ButtonPropsWithoutChildren> = (
  props
) => (
  <StyledButton leftIcon={<FiEdit />} {...props}>
    編集
  </StyledButton>
);

/**
 * 保存ボタン
 * @param props -
 * @returns
 */
export const SaveButton: React.FunctionComponent<ButtonPropsWithoutChildren> = (
  props
) => (
  <StyledButton leftIcon={<FiSave />} type="submit" {...props}>
    保存
  </StyledButton>
);

/**
 * 検索ボタン
 * @param props -
 * @returns
 */
export const SearchButton: React.FunctionComponent<
  ButtonPropsWithoutChildren
> = (props) => (
  <StyledButton leftIcon={<FiSearch />} type="submit" {...props}>
    検索
  </StyledButton>
);

/**
 * 選択ボタン
 * @param props -
 * @returns
 */
export const SelectButton: React.FunctionComponent<
  ButtonPropsWithoutChildren
> = (props) => (
  <CheckButton type="submit" {...props}>
    選択
  </CheckButton>
);

type ViewButtonProps = DownloadButtonProps & {
  onOpenImageModal: Consumer<EngeneFileObject>;
  onError?: MutateOptions<
    void | DownloadAttachedResult,
    unknown,
    DownloadAttachedFileQuery[],
    unknown
  >['onError'];
};

/**
 * 閲覧ボタン
 *
 * @param param0 -
 * @returns
 */
export const ViewButton: React.FunctionComponent<ViewButtonProps> = ({
  fileName,
  onError,
  onOpenImageModal,
  query,
  ...props
}) => {
  const getAttachedFile = useGetAttachedFile();
  const { toast } = useToastHook();

  return (
    <StyledButton
      leftIcon={<FiEye />}
      onClick={() =>
        getAttachedFile.mutate([query], {
          onSuccess: (results) => {
            if (Array.isArray(results)) {
              const obj = new EngeneFileObject(
                fileName || results[0].filename,
                results[0].file.size,
                query.id
              );
              obj.file = results[0].file;
              onOpenImageModal(obj);
            }
          },
          onError:
            onError ||
            ((error) =>
              error instanceof AppError
                ? error.status === 400
                  ? toast('閲覧できないファイル種別です', 'warning')
                  : toast(error)
                : toast('ファイルダウンロードに失敗しました', 'warning')),
        })
      }
      {...props}
    >
      閲覧
    </StyledButton>
  );
};

export default StyledButton;
